import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

/**
 * Rascunho da nota nova, por conversa. Mora no cache do QueryClient e NÃO em localStorage nem em
 * variável de módulo: a chave não é a da sessão, então o logout e a troca de conta descartam o
 * rascunho junto com o resto dos dados da pessoa (`purgeUserData`) e ele nunca vaza para o
 * próximo usuário da mesma aba.
 */
export const noteDraftKey = (conversationId: string) => ['draft', 'note', conversationId] as const;

/**
 * Lê o rascunho ao montar e devolve `save` para gravá-lo a cada alteração. O campo de texto guarda
 * o valor em estado próprio (um campo controlado pelo cache atrasaria o texto digitado: o React
 * Query notifica depois do evento); o cache recebe uma cópia.
 */
export function useNoteDraft(conversationId: string): {
  initial: string;
  save: (text: string) => void;
} {
  const queryClient = useQueryClient();

  // Um consulta que ninguém observa é descartada em 5 minutos, e o rascunho de uma conversa
  // deixada para trás sumiria. Este observador (que nunca busca nada: `skipToken`, e nunca
  // re-renderiza: `notifyOnChangeProps: []`) só fixa `gcTime: Infinity`; quem limpa é o logout.
  useQuery<string>({
    queryKey: noteDraftKey(conversationId),
    queryFn: skipToken,
    gcTime: Infinity,
    staleTime: Infinity,
    notifyOnChangeProps: [],
  });

  const [initial] = useState(
    () => queryClient.getQueryData<string>(noteDraftKey(conversationId)) ?? '',
  );

  const save = useCallback(
    (text: string): void => {
      queryClient.setQueryData<string>(noteDraftKey(conversationId), text);
    },
    [queryClient, conversationId],
  );

  return { initial, save };
}
